import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  BookOpen,
  Calculator,
  Save,
  CheckCircle2,
  X,
} from "lucide-react";
import {
  updateExamPassword,
  getTheologyAccess,
  updateTheologyAccess,
  getMathAccess,
  updateMathAccess,
  updateVocabDeletePassword,
} from "../../services/settingsService";

/* ── Toggle ──────────────────────────────────────────── */
function Toggle({ checked, onChange, label }) {
  return (
    <label className="admin-toggle-wrap" onClick={() => onChange(!checked)} style={{ cursor: 'pointer' }}>
      <span
        style={{
          position: 'relative',
          display: 'inline-block',
          width: 36,
          height: 20,
          borderRadius: 20,
          background: checked ? 'var(--admin-primary)' : '#cbd5e1',
          transition: 'background 0.2s',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 2,
            top: 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1)',
            transform: checked ? 'translateX(16px)' : 'translateX(0)',
          }}
        />
      </span>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--admin-text-primary)', userSelect: 'none' }}>
        {label}
      </span>
    </label>
  );
}

/* ── Success Toast ────────────────────────────────────── */
function SuccessToast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [message, onClose]);

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="admin-toast"
          role="alert"
        >
          <CheckCircle2 size={18} className="admin-toast-icon" />
          <span>{message}</span>
          <button onClick={onClose} className="admin-toast-close">
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── Access Settings Block ───────────────────────────── */
function AccessBlock({
  title,
  description,
  icon: Icon,
  color,
  required,
  setRequired,
  password,
  setPassword,
  loading,
  onSave,
}) {
  return (
    <div className={`admin-access-block admin-access-block--${color}`}>
      <div className="admin-access-block-header">
        <div className={`admin-access-block-icon admin-access-icon--${color}`}>
          <Icon size={18} strokeWidth={2} />
        </div>
        <div>
          <h3 className="admin-access-block-title">{title}</h3>
          <p className="admin-access-block-sub">
            {required ? "🔒 Password required" : "🔓 Open access"}
          </p>
        </div>
        <span
          className={`admin-access-badge ${required ? "admin-access-badge--locked" : "admin-access-badge--open"
            }`}
        >
          {required ? "Locked" : "Unlocked"}
        </span>
      </div>
      <p className="admin-access-desc">{description}</p>
      <div className="admin-access-controls">
        <Toggle checked={required} onChange={setRequired} label="Require password" />
        <div className="admin-access-pin-row">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value.replace(/\D/g, ""))}
            disabled={!required}
            className="admin-input admin-pin-input"
            placeholder="6-digit PIN"
          />
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onSave}
            disabled={
              loading ||
              (required && password.length > 0 && password.length !== 6)
            }
            className={`admin-btn admin-btn--${color}`}
          >
            <Save size={14} />
            {loading ? "Saving…" : "Save"}
          </motion.button>
        </div>
        <p className="admin-access-hint">
          Leave blank to keep current PIN. Enter a new 6-digit PIN to update.
        </p>
      </div>
    </div>
  );
}

/* ── Math Access Settings Panel ───────────────────────── */
export function MathAccessSettings() {
  const [mathPwd, setMathPwd] = useState("");
  const [mathRequired, setMathRequired] = useState(true);
  const [mathLoading, setMathLoading] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    getMathAccess().then((s) => setMathRequired(s.requirePassword !== false));
  }, []);

  const handleSaveMath = async () => {
    if (mathRequired && mathPwd && !/^\d{6}$/.test(mathPwd)) {
      alert("Password must be exactly 6 digits.");
      return;
    }
    setMathLoading(true);
    try {
      await updateMathAccess({
        password: mathPwd,
        requirePassword: mathRequired,
      });
      setToast("Math access settings updated successfully!");
      setMathPwd("");
    } catch (err) {
      alert(err.message || "Failed to update math access settings.");
    } finally {
      setMathLoading(false);
    }
  };

  return (
    <div className="admin-settings">
      <div className="admin-settings-header">
        <h1 className="admin-overview-title">Math Access Settings</h1>
        <p className="admin-overview-subtitle">
          Manage PIN access control for the Math course library.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ maxWidth: 520 }}
      >
        <AccessBlock
          title="Math Access"
          description="Control whether the Math study card requires a PIN before opening."
          icon={Calculator}
          color="green"
          required={mathRequired}
          setRequired={setMathRequired}
          password={mathPwd}
          setPassword={setMathPwd}
          loading={mathLoading}
          onSave={handleSaveMath}
        />
      </motion.div>

      <SuccessToast message={toast} onClose={() => setToast("")} />
    </div>
  );
}

/* ── Theology Access Settings Panel ────────────────────── */
export function TheologyAccessSettings() {
  const [theologyPwd, setTheologyPwd] = useState("");
  const [theologyRequired, setTheologyRequired] = useState(true);
  const [theologyLoading, setTheologyLoading] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    getTheologyAccess().then((s) =>
      setTheologyRequired(s.requirePassword !== false)
    );
  }, []);

  const handleSaveTheology = async () => {
    if (theologyRequired && theologyPwd && !/^\d{6}$/.test(theologyPwd)) {
      alert("Password must be exactly 6 digits.");
      return;
    }
    setTheologyLoading(true);
    try {
      await updateTheologyAccess({
        password: theologyPwd,
        requirePassword: theologyRequired,
      });
      setToast("Theology access settings updated successfully!");
      setTheologyPwd("");
    } catch (err) {
      alert(err.message || "Failed to update theology access settings.");
    } finally {
      setTheologyLoading(false);
    }
  };

  return (
    <div className="admin-settings">
      <div className="admin-settings-header">
        <h1 className="admin-overview-title">Theology Access Settings</h1>
        <p className="admin-overview-subtitle">
          Manage PIN access control for Theology study notes.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ maxWidth: 520 }}
      >
        <AccessBlock
          title="Theology Access"
          description="Control whether the Theology study card requires a PIN before opening."
          icon={BookOpen}
          color="blue"
          required={theologyRequired}
          setRequired={setTheologyRequired}
          password={theologyPwd}
          setPassword={setTheologyPwd}
          loading={theologyLoading}
          onSave={handleSaveTheology}
        />
      </motion.div>

      <SuccessToast message={toast} onClose={() => setToast("")} />
    </div>
  );
}

/* ── Spanish Exam Password Settings Panel ────────────────── */
export function SpanishExamPasswordSettings() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [vocabDeletePwd, setVocabDeletePwd] = useState("");
  const [vocabDeleteLoading, setVocabDeleteLoading] = useState(false);
  const [toast, setToast] = useState("");

  const handleSaveExam = async () => {
    const pwdList = password
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p);
    if (pwdList.length === 0 || !pwdList.every((p) => /^\d{6}$/.test(p))) {
      alert("All passwords must be 6-digit numbers separated by commas.");
      return;
    }
    setLoading(true);
    try {
      await updateExamPassword(password);
      setToast("Spanish exam generation password updated successfully!");
      setPassword("");
    } catch {
      alert("Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVocabDelete = async () => {
    if (!/^\d{6}$/.test(vocabDeletePwd)) {
      alert("Vocab delete password must be exactly 6 digits.");
      return;
    }
    setVocabDeleteLoading(true);
    try {
      await updateVocabDeletePassword(vocabDeletePwd);
      setToast("Vocabulary unit delete password updated successfully!");
      setVocabDeletePwd("");
    } catch (err) {
      alert(err.message || "Failed to update vocab delete password.");
    } finally {
      setVocabDeleteLoading(false);
    }
  };

  return (
    <div className="admin-settings">
      <div className="admin-settings-header">
        <h1 className="admin-overview-title">Spanish Exam Password Settings</h1>
        <p className="admin-overview-subtitle">
          Manage authorization PINs for Spanish exam generation and vocab management.
        </p>
      </div>

      {/* Exam Generation Password */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="admin-settings-section"
        style={{ maxWidth: 640 }}
      >
        <div className="admin-settings-section-header">
          <div className="admin-access-block-icon admin-access-icon--blue">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h2 className="admin-card-title">Exam Generation Password</h2>
            <p className="admin-overview-subtitle" style={{ marginTop: 2 }}>
              Comma-separated 6-digit PINs for exam generation access.
            </p>
          </div>
        </div>
        <div className="admin-exam-pwd-row">
          <input
            type="text"
            value={password}
            onChange={(e) =>
              setPassword(e.target.value.replace(/[^\d,\s]/g, ""))
            }
            className="admin-input"
            placeholder="123456, 654321"
          />
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleSaveExam}
            disabled={loading || password.trim().length === 0}
            className="admin-btn admin-btn--green"
          >
            <Save size={14} />
            {loading ? "Saving…" : "Save"}
          </motion.button>
        </div>
      </motion.div>

      {/* Vocab Unit Delete Password */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.08 }}
        className="admin-settings-section"
        style={{ maxWidth: 640 }}
      >
        <div className="admin-settings-section-header">
          <div className="admin-access-block-icon admin-access-icon--blue">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h2 className="admin-card-title">Unit Vocab Delete Password</h2>
            <p className="admin-overview-subtitle" style={{ marginTop: 2 }}>
              6-digit PIN required to delete a vocabulary unit from the vocab list page. Valid for 5 minutes once entered.
            </p>
          </div>
        </div>
        <div className="admin-exam-pwd-row">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={vocabDeletePwd}
            onChange={(e) => setVocabDeletePwd(e.target.value.replace(/\D/g, ""))}
            className="admin-input admin-pin-input"
            placeholder="6-digit PIN"
          />
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleSaveVocabDelete}
            disabled={vocabDeleteLoading || vocabDeletePwd.length !== 6}
            className="admin-btn admin-btn--green"
          >
            <Save size={14} />
            {vocabDeleteLoading ? "Saving…" : "Save"}
          </motion.button>
        </div>
      </motion.div>

      <SuccessToast message={toast} onClose={() => setToast("")} />
    </div>
  );
}

/* ── Main Settings Panel (Empty Page) ─────────────────────── */
export default function AdminSettings() {
  return (
    <div className="admin-settings">
      <div className="admin-settings-header">
        <h1 className="admin-overview-title">Settings</h1>
        <p className="admin-overview-subtitle">
          Global platform configuration.
        </p>
      </div>

      <div
        className="admin-settings-section"
        style={{
          minHeight: 240,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--admin-text-muted)",
          textAlign: "center",
          padding: 40,
        }}
      >
        <div>
          <h3 style={{ margin: "0 0 8px 0", color: "var(--admin-text-primary)", fontSize: 16 }}>
            Settings Page
          </h3>
          <p style={{ margin: 0, fontSize: 13 }}>
            Access controls and module passwords have been moved to their respective Study menus:
            <br />
            <strong>Study → Math → Math Access</strong>
            <br />
            <strong>Study → Theology → Theology Access</strong>
            <br />
            <strong>Study → CS → Data Science Password</strong>
            <br />
            <strong>Study → Spanish → Exam Password</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
